//src/components/gro/SurveyAudiencePreview.tsx
import {
  Copy,
  History,
  Mail,
  Power,
  RotateCcw,
  UserPlus,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useMemo, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";
import { PERMISSIONS } from "@/lib/permissions";
import type { SurveyCampaignAudiencePreviewItem } from "@/types/gro";

type Props = {
  items: SurveyCampaignAudiencePreviewItem[];
  unitsMap?: Record<string, string>;
  departmentsMap?: Record<string, string>;
  jobPositionsMap?: Record<string, string>;
  showSensitiveColumns?: boolean;
  allowCopySharedLink?: boolean;
  sharedSurveyToken?: string | null;
  canManageParticipants?: boolean;
  canResendEmail?: boolean;
  onAddParticipant?: () => void;
  onDeactivateParticipant?: (item: SurveyCampaignAudiencePreviewItem) => void;
  onReactivateParticipant?: (item: SurveyCampaignAudiencePreviewItem) => void;
  onOpenHistory?: (item: SurveyCampaignAudiencePreviewItem) => void;
  onResendEmail?: (item: SurveyCampaignAudiencePreviewItem) => void;
  onResetEmailStatus?: (item: SurveyCampaignAudiencePreviewItem) => void;
  headerActions?: ReactNode;
};

function tokenStatusLabel(status?: string | null) {
  if (!status) return "—";
  if (status === "pending") return "Pendente";
  if (status === "sent") return "Enviado";
  if (status === "failed") return "Falhou";
  if (status === "responded") return "Respondido";
  if (status === "expired") return "Expirado";
  if (status === "cancelled") return "Cancelado";
  return status;
}

function emailStatusLabel(status?: string | null) {
  if (!status) return "—";
  if (status === "pending") return "Pendente";
  if (status === "processing") return "Processando";
  if (status === "sent") return "Enviado";
  if (status === "failed") return "Falhou";
  if (status === "cancelled") return "Cancelado";
  if (status === "expired") return "Expirado";
  return status;
}

function emailStatusVariant(status?: string | null): "default" | "secondary" | "outline" | "destructive" {
  if (status === "sent") return "default";
  if (status === "failed") return "destructive";
  if (status === "pending" || status === "processing") return "secondary";
  return "outline";
}

function buildPublicSurveyUrl(token?: string | null) {
  if (!token) return "";
  if (typeof window === "undefined") return `/public/surveys/${token}`;
  return `${window.location.origin}/public/surveys/${token}`;
}

function normalizeText(value?: string | null) {
  return (value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

export function SurveyAudiencePreview({
  items,
  unitsMap = {},
  departmentsMap = {},
  jobPositionsMap = {},
  showSensitiveColumns = false,
  allowCopySharedLink = false,
  sharedSurveyToken = null,
  canManageParticipants = false,
  canResendEmail = false,
  onAddParticipant,
  onDeactivateParticipant,
  onReactivateParticipant,
  onOpenHistory,
  onResendEmail,
  onResetEmailStatus,
  headerActions,
}: Props) {
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const nameA = normalizeText(a.name);
      const nameB = normalizeText(b.name);

      if (nameA && nameB && nameA !== nameB) {
        return nameA.localeCompare(nameB, "pt-BR");
      }

      if (nameA && !nameB) return -1;
      if (!nameA && nameB) return 1;

      const emailA = normalizeText(a.email);
      const emailB = normalizeText(b.email);

      return emailA.localeCompare(emailB, "pt-BR");
    });
  }, [items]);

  async function handleCopyLink(token?: string | null) {
    if (!token) return;

    try {
      await navigator.clipboard.writeText(buildPublicSurveyUrl(token));
      toast.success("Link da pesquisa copiado.");
    } catch {
      toast.error("Não foi possível copiar o link da pesquisa.");
    }
  }

  const canCopyLink = showSensitiveColumns || allowCopySharedLink;
  const showActionsColumn = canCopyLink || canManageParticipants || canResendEmail;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Público-alvo
            <Badge variant="outline">{sortedItems.length}</Badge>
          </CardTitle>

          <div className="flex items-center gap-2">
            {headerActions}

            {canManageParticipants && onAddParticipant ? (
              <PermissionIconButton
                label="Adicionar participante"
                permission={PERMISSIONS.SURVEY_CAMPAIGNS_WRITE}
                onClick={onAddParticipant}
              >
                <UserPlus className="h-4 w-4" />
              </PermissionIconButton>
            ) : null}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {sortedItems.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            Nenhum participante encontrado para os filtros selecionados.
          </div>
        ) : (
          <div className="max-h-[420px] overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Unidade</TableHead>
                  <TableHead>Departamento</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Status do e-mail</TableHead>
                                    {showSensitiveColumns ? <TableHead>Status</TableHead> : null}
                  {showActionsColumn ? (
                    <TableHead className="w-[220px] text-right">Ações</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>

              <TableBody>
                {sortedItems.map((item) => {
                  const typedItem = item as any;
                  const isCancelled = item.delivery_status === "cancelled";
                  const emailDeliveryStatus = typedItem.email_delivery_status as string | null | undefined;
                  const canResetToPending = emailDeliveryStatus === "failed" && !!onResetEmailStatus;

                  return (
                    <TableRow key={item.recipient_id}>
                      <TableCell>
                        <div className="font-medium">{item.name || "Sem nome"}</div>
                        <div className="text-sm text-muted-foreground">
                          {item.email || "Sem e-mail"}
                        </div>
                      </TableCell>

                      <TableCell>
                        {item.unit_id ? unitsMap[item.unit_id] || "—" : "—"}
                      </TableCell>

                      <TableCell>
                        {item.department_id
                          ? departmentsMap[item.department_id] || "—"
                          : "—"}
                      </TableCell>

                      <TableCell>
                        {item.job_position_id
                          ? jobPositionsMap[item.job_position_id] || "—"
                          : "—"}
                      </TableCell>

                      <TableCell>
                        <div className="flex flex-col items-start gap-2">
                          <Badge variant={emailStatusVariant(emailDeliveryStatus)}>
                            {emailStatusLabel(emailDeliveryStatus)}
                          </Badge>
                          {canResetToPending ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={() => onResetEmailStatus?.(item)}
                            >
                              Marcar pendente
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>

                                            {showSensitiveColumns ? (
                        <TableCell>{tokenStatusLabel(item.delivery_status)}</TableCell>
                      ) : null}

                      {showActionsColumn ? (
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {canCopyLink ? (
                              <PermissionIconButton
                                label="Copiar link da pesquisa"
                                permission={PERMISSIONS.SURVEY_CAMPAIGNS_WRITE}
                                onClick={() => handleCopyLink(showSensitiveColumns ? item.respondent_token : sharedSurveyToken)}
                                disabled={!(showSensitiveColumns ? item.respondent_token : sharedSurveyToken)}
                              >
                                <Copy className="h-4 w-4" />
                              </PermissionIconButton>
                            ) : null}

                            {canResendEmail && onResendEmail ? (
                              <PermissionIconButton
                                label="Reenviar convite por e-mail"
                                permission={PERMISSIONS.SURVEY_CAMPAIGNS_WRITE}
                                onClick={() => onResendEmail(item)}
                                disabled={!item.email || isCancelled}
                              >
                                <Mail className="h-4 w-4" />
                              </PermissionIconButton>
                            ) : null}

                            {onOpenHistory ? (
                              <PermissionIconButton
                                label="Histórico"
                                permission={PERMISSIONS.SURVEY_CAMPAIGNS_WRITE}
                                onClick={() => onOpenHistory(item)}
                              >
                                <History className="h-4 w-4" />
                              </PermissionIconButton>
                            ) : null}

                            {canManageParticipants && onDeactivateParticipant && !isCancelled ? (
                              <PermissionIconButton
                                label="Desativar participante"
                                permission={PERMISSIONS.SURVEY_CAMPAIGNS_WRITE}
                                onClick={() => onDeactivateParticipant(item)}
                              >
                                <Power className="h-4 w-4" />
                              </PermissionIconButton>
                            ) : null}

                            {canManageParticipants && onReactivateParticipant && isCancelled ? (
                              <PermissionIconButton
                                label="Reativar participante"
                                permission={PERMISSIONS.SURVEY_CAMPAIGNS_WRITE}
                                onClick={() => onReactivateParticipant(item)}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </PermissionIconButton>
                            ) : null}
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
